// Small, dependency-free SVG charts for /admin.
//
// ── Why hand-drawn ──────────────────────────────────────────────────────────
// Recharts or Chart.js would each be a larger download than the whole rest of
// this page, for four fixed charts that never need zooming, brushing or a
// second axis. The same reasoning as utils/xlsx.js and utils/qrcode.js.
//
// ── Colour ──────────────────────────────────────────────────────────────────
// ⚠️ No new colour language. Every fill here is a tint of the #1a73e8 the
// dashboard already uses for its active tab and primary button, plus the greys
// it already uses for secondary text. A chart that introduced its own palette
// would be the most colourful thing on a deliberately plain page.
//
// ── Reading them ────────────────────────────────────────────────────────────
// Every mark carries a <title>, so hovering gives the exact number without any
// tooltip machinery, and screen readers get the same text. The wrapper is
// role="img" with a written-out summary, because a chart nobody can read is a
// decoration.

const BLUE = '#1a73e8';
// Light → dark, so a legend read top to bottom gets steadily stronger and the
// biggest slice is the darkest.
const SERIES = ['#1a73e8', '#4285f4', '#669df6', '#8ab4f8', '#aecbfa', '#c6dafc', '#d2e3fc'];
const GREY = '#9aa0a6';
const AXIS = '#5f6368';
const GRID = '#e8eaed';

/** "Skipped" is not a category like the others — it is the absence of one. */
const colorFor = (label, i) => (/^skipped$/i.test(label) ? GREY : SERIES[i % SERIES.length]);

const nf = (n) => Number(n || 0).toLocaleString('en-CA');

// ── Shared frame ────────────────────────────────────────────────────────────

export function ChartCard({ title, hint, children, empty }) {
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>{title}</div>
      {hint && <div style={S.cardHint}>{hint}</div>}
      {empty ? <div style={S.empty}>{empty}</div> : children}
    </div>
  );
}

// ── Trend: a value per day ──────────────────────────────────────────────────

/**
 * `points` is `[{ day: '2026-09-09', value: 12 }]`, OLDEST FIRST.
 *
 * ⚠️ The caller reverses. Every store here returns days newest-first (that is
 * what the tables want), and a line drawn in that order runs backwards through
 * time without looking wrong — which is the sort of chart somebody quotes in a
 * meeting before anybody notices.
 *
 * `cumulative` draws the running total instead, which is what "total signups
 * over time" actually means: a per-day line answers "how busy was Tuesday", a
 * cumulative one answers "how many people do we have now".
 */
export function TrendChart({ points, cumulative = false, height = 168, label = 'per day' }) {
  const raw = points || [];
  if (!raw.length) return null;

  let running = 0;
  const series = raw.map((p) => {
    running += Number(p.value) || 0;
    return { day: p.day, value: cumulative ? running : (Number(p.value) || 0) };
  });

  const W = 720;
  const H = height;
  const padL = 44;
  const padR = 10;
  const padT = 10;
  const padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const max = Math.max(1, ...series.map((p) => p.value));
  // A rounded ceiling, so the gridline labels are numbers a person would say.
  const step = niceStep(max);
  const top = Math.max(step, Math.ceil(max / step) * step);

  // A single day has no width to spread across; centre it rather than dividing
  // by zero.
  const x = (i) => (series.length === 1
    ? padL + innerW / 2
    : padL + (i / (series.length - 1)) * innerW);
  const y = (v) => padT + innerH - (v / top) * innerH;

  const line = series.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join('');
  const area = `${line}L${x(series.length - 1).toFixed(1)},${(padT + innerH).toFixed(1)}`
    + `L${x(0).toFixed(1)},${(padT + innerH).toFixed(1)}Z`;

  const gridlines = [0, 0.5, 1].map((f) => Math.round(top * f));
  // At most six date labels, or they overlap and stop being readable.
  const labelEvery = Math.max(1, Math.ceil(series.length / 6));

  const last = series[series.length - 1];
  const summary = `${label}: ${series.length} day${series.length === 1 ? '' : 's'}, `
    + `latest ${nf(last.value)}, peak ${nf(max)}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={S.svg} role="img" aria-label={summary}>
      {/* Keyed by position: at a small top the rounded values repeat (0, 1, 1). */}
      {gridlines.map((g, gi) => (
        <g key={gi}>
          <line x1={padL} x2={W - padR} y1={y(g)} y2={y(g)} stroke={GRID} strokeWidth="1" />
          <text x={padL - 8} y={y(g) + 4} textAnchor="end" fontSize="11" fill={AXIS}>{nf(g)}</text>
        </g>
      ))}

      <path d={area} fill={BLUE} opacity="0.10" />
      <path d={line} fill="none" stroke={BLUE} strokeWidth="2.5"
            strokeLinejoin="round" strokeLinecap="round" />

      {series.map((p, i) => (
        <g key={p.day}>
          {/* A wide invisible target, so the <title> is reachable without
              having to land on a 3px dot. */}
          <circle cx={x(i)} cy={y(p.value)} r="10" fill="transparent">
            <title>{`${p.day}: ${nf(p.value)}`}</title>
          </circle>
          {(series.length <= 60 || i === series.length - 1) && (
            <circle cx={x(i)} cy={y(p.value)} r={i === series.length - 1 ? 3.5 : 2.5} fill={BLUE} />
          )}
        </g>
      ))}

      {series.map((p, i) => (
        (i % labelEvery === 0 || i === series.length - 1) ? (
          <text key={`l${p.day}`} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10.5" fill={AXIS}>
            {shortDay(p.day)}
          </text>
        ) : null
      ))}
    </svg>
  );
}

/** "2026-09-09" → "Sep 9". The year is the same for every point on screen. */
function shortDay(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return String(day || '');
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MON[Number(m[2]) - 1] || ''} ${Number(m[3])}`;
}

/** A gridline step a person would choose: 1, 2, 5, 10, 20, 50 … */
function niceStep(max) {
  const rough = Math.max(1, max) / 2;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * mag;
}

// ── Bars: a value per hour of the day ───────────────────────────────────────

/** `values` is 24 numbers, midnight first. */
export function HourChart({ values, height = 150 }) {
  const v = (values || []).slice(0, 24);
  while (v.length < 24) v.push(0);
  const total = v.reduce((n, x) => n + x, 0);
  if (!total) return null;

  const W = 720;
  const H = height;
  const padL = 44;
  const padR = 10;
  const padT = 10;
  const padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const max = Math.max(...v);
  const step = niceStep(max);
  const top = Math.max(step, Math.ceil(max / step) * step);
  const slot = innerW / 24;
  const barW = Math.max(4, slot - 5);
  const busiest = v.indexOf(max);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={S.svg} role="img"
         aria-label={`By hour of day. Busiest hour ${hourLabel(busiest)} with ${nf(max)}.`}>
      {[0, 0.5, 1].map((f) => {
        const g = Math.round(top * f);
        const yy = padT + innerH - (g / top) * innerH;
        return (
          <g key={f}>
            <line x1={padL} x2={W - padR} y1={yy} y2={yy} stroke={GRID} strokeWidth="1" />
            <text x={padL - 8} y={yy + 4} textAnchor="end" fontSize="11" fill={AXIS}>{nf(g)}</text>
          </g>
        );
      })}

      {v.map((n, h) => {
        const bh = top ? (n / top) * innerH : 0;
        const bx = padL + h * slot + (slot - barW) / 2;
        return (
          <rect
            key={h}
            x={bx}
            y={padT + innerH - bh}
            width={barW}
            // A visible sliver for a genuine zero would be a lie; a zero-height
            // rect is correct and still carries its <title>.
            height={Math.max(0, bh)}
            rx="2"
            // The busiest hour is the answer somebody came for, so it is the
            // only bar in the full-strength blue.
            fill={h === busiest ? BLUE : '#8ab4f8'}
          >
            <title>{`${hourLabel(h)}: ${nf(n)}`}</title>
          </rect>
        );
      })}

      {/* Every third hour, so the axis reads without crowding. */}
      {v.map((_, h) => (h % 3 === 0 ? (
        <text key={`t${h}`} x={padL + h * slot + slot / 2} y={H - 7}
              textAnchor="middle" fontSize="10.5" fill={AXIS}>
          {shortHour(h)}
        </text>
      ) : null))}
    </svg>
  );
}

const shortHour = (h) => (h === 0 ? '12a' : h === 12 ? '12p' : h < 12 ? `${h}a` : `${h - 12}p`);
const hourLabel = (h) => `${shortHour(h)}–${shortHour((h + 1) % 24)}`;

// ── Donut: shares of a whole ────────────────────────────────────────────────

/**
 * `slices` is `[{ label, value }]`. Zero-valued slices are kept in the legend
 * and drawn as nothing — on this dashboard a zero is a real answer ("nobody
 * chose it"), and dropping the row would read as the option not existing.
 */
export function DonutChart({ slices, size = 168, unit = 'people' }) {
  const list = (slices || []).map((s) => ({ ...s, value: Number(s.value) || 0 }));
  const total = list.reduce((n, s) => n + s.value, 0);
  if (!total) return null;

  const R = size / 2;
  const stroke = size * 0.26;
  const r = R - stroke / 2;
  const C = 2 * Math.PI * r;

  let offset = 0;
  const arcs = list.map((s, i) => {
    const frac = s.value / total;
    const arc = {
      ...s,
      color: colorFor(s.label, i),
      dash: `${(frac * C).toFixed(2)} ${(C - frac * C).toFixed(2)}`,
      // Rotated by the running total so each arc starts where the last ended;
      // -90 puts the first slice at twelve o'clock.
      rotate: -90 + offset * 360,
      pct: Math.round(frac * 1000) / 10,
    };
    offset += frac;
    return arc;
  });

  return (
    <div style={S.donutRow}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}
           role="img" aria-label={arcs.map((a) => `${a.label} ${a.pct}%`).join(', ')}>
        <g transform={`translate(${R},${R})`}>
          {arcs.map((a) => (
            a.value > 0 && (
              <circle
                key={a.label}
                r={r} fill="none" stroke={a.color} strokeWidth={stroke}
                strokeDasharray={a.dash} transform={`rotate(${a.rotate})`}
              >
                <title>{`${a.label}: ${nf(a.value)} (${a.pct}%)`}</title>
              </circle>
            )
          ))}
          <text textAnchor="middle" y="-2" fontSize="21" fontWeight="800" fill="#202124">{nf(total)}</text>
          <text textAnchor="middle" y="15" fontSize="11" fill={AXIS}>{unit}</text>
        </g>
      </svg>
      <ul style={S.legend}>
        {arcs.map((a) => (
          <li key={a.label} style={S.legendItem}>
            <span style={{ ...S.swatch, background: a.color }} />
            <span style={S.legendLabel}>{a.label}</span>
            <span style={S.legendValue}>{nf(a.value)}</span>
            <span style={S.legendPct}>{a.pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Horizontal bars: a ranked list ──────────────────────────────────────────

/**
 * `items` is `[{ label, value, sub? }]`, already in the order to draw.
 *
 * For "which restaurant", "which dish", "what did they search": a ranked list
 * of named things, where a pie of twenty slices is a colour wheel and a column
 * chart makes the names unreadable. Plain HTML rather than SVG, so a long name
 * wraps and ellipsises like text instead of being clipped by a viewBox.
 *
 * One hue at every length — this is magnitude, not identity, so colour carries
 * nothing a bar's length does not already say. `max` caps the rows and says
 * how many were left out, rather than silently truncating.
 */
export function BarList({ items, max = 10, unit = '', emptyText = null }) {
  const list = (items || []).filter((x) => Number(x.value) > 0);
  if (!list.length) return emptyText ? <div style={S.empty}>{emptyText}</div> : null;
  const shown = list.slice(0, max);
  const top = Math.max(1, ...shown.map((x) => Number(x.value) || 0));
  return (
    <div style={S.barList} role="list">
      {shown.map((x, i) => {
        const v = Number(x.value) || 0;
        const tip = `${x.label}${x.sub ? ` (${x.sub})` : ''}: ${nf(v)}${unit ? ` ${unit}` : ''}`;
        return (
          <div key={`${x.label}-${i}`} style={S.barRow} role="listitem" title={tip}>
            <div style={S.barLabel}>
              <span style={S.barName}>{x.label}</span>
              {x.sub && <span style={S.barSub}>{x.sub}</span>}
            </div>
            <div style={S.barTrack}>
              <div style={{ ...S.barFill, width: `${Math.max(1.5, (v / top) * 100)}%`, opacity: i === 0 ? 1 : 0.72 }} />
            </div>
            <div style={S.barValue}>{nf(v)}</div>
          </div>
        );
      })}
      {list.length > shown.length && (
        <div style={S.barMore}>…and {nf(list.length - shown.length)} more in the download.</div>
      )}
    </div>
  );
}

// ── Columns: a value per labelled bucket (weeks) ────────────────────────────

/**
 * `bars` is `[{ label, value, title? }]`, OLDEST FIRST. For per-week totals,
 * where a line would imply a smooth change between Saturday and Sunday that a
 * weekly bucket cannot show. The latest bar is full-strength blue, the rest a
 * tint, so "this week" is the one the eye lands on.
 */
export function ColumnChart({ bars, height = 168, label = 'per week' }) {
  const v = (bars || []).map((b) => ({ ...b, value: Number(b.value) || 0 }));
  if (!v.length || !v.some((b) => b.value > 0)) return null;
  const W = 720;
  const H = height;
  const padL = 44;
  const padR = 10;
  const padT = 10;
  const padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = Math.max(...v.map((b) => b.value));
  const step = niceStep(max);
  const top = Math.max(step, Math.ceil(max / step) * step);
  const slot = innerW / v.length;
  const barW = Math.max(3, Math.min(48, slot - 4));
  const labelEvery = Math.max(1, Math.ceil(v.length / 8));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={S.svg} role="img"
         aria-label={`${label}: ${v.length} bars, latest ${nf(v[v.length - 1].value)}, peak ${nf(max)}`}>
      {[0, 0.5, 1].map((f) => {
        const g = Math.round(top * f);
        const yy = padT + innerH - (g / top) * innerH;
        return (
          <g key={f}>
            <line x1={padL} x2={W - padR} y1={yy} y2={yy} stroke={GRID} strokeWidth="1" />
            <text x={padL - 8} y={yy + 4} textAnchor="end" fontSize="11" fill={AXIS}>{nf(g)}</text>
          </g>
        );
      })}
      {v.map((b, i) => {
        const bh = (b.value / top) * innerH;
        return (
          <g key={`${b.label}-${i}`}>
            {/* The full-height slot is the hover target, not the bar: a short
                bar is otherwise a 3px thing to land on. */}
            <rect x={padL + i * slot} y={padT} width={slot} height={innerH} fill="transparent">
              <title>{b.title || `${b.label}: ${nf(b.value)}`}</title>
            </rect>
            <rect x={padL + i * slot + (slot - barW) / 2} y={padT + innerH - bh} width={barW}
                  height={Math.max(0, bh)} rx="2" fill={i === v.length - 1 ? BLUE : '#8ab4f8'}
                  pointerEvents="none" />
          </g>
        );
      })}
      {v.map((b, i) => ((i % labelEvery === 0 || i === v.length - 1) ? (
        <text key={`t${i}`} x={padL + i * slot + slot / 2} y={H - 8} textAnchor="middle" fontSize="10.5" fill={AXIS}>
          {b.label}
        </text>
      ) : null))}
    </svg>
  );
}

const S = {
  card: {
    background: '#fff', borderRadius: 12, padding: '13px 15px 10px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', minWidth: 0,
  },
  cardTitle: { fontSize: 13.5, fontWeight: 800, color: '#202124' },
  cardHint: { fontSize: 12, color: '#5f6368', marginTop: 2, lineHeight: 1.4 },
  svg: { display: 'block', width: '100%', height: 'auto', marginTop: 10 },
  empty: { fontSize: 13, color: '#5f6368', padding: '18px 0 14px' },
  donutRow: { display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 },
  legend: { listStyle: 'none', margin: 0, padding: 0, flex: 1, minWidth: 168 },
  legendItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '2.5px 0' },
  swatch: { width: 10, height: 10, borderRadius: 2, flexShrink: 0 },
  legendLabel: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  legendValue: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  legendPct: { color: '#5f6368', width: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  barList: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 },
  barRow: { display: 'grid', gridTemplateColumns: 'minmax(90px, 38%) 1fr auto', alignItems: 'center', gap: 10, fontSize: 12.5 },
  barLabel: { minWidth: 0, display: 'flex', flexDirection: 'column' },
  barName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#202124' },
  barSub: { fontSize: 11, color: '#5f6368', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  barTrack: { height: 10, background: '#f1f3f4', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: '100%', background: BLUE, borderRadius: 4 },
  barValue: { fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'right' },
  barMore: { fontSize: 12, color: '#5f6368', marginTop: 2 },
};
